/**
 * OI Historical Backfill page -- "Breeze Data Integrity Audit" (admin).
 *
 * READ-ONLY audit of the stored historical market data before it is used for backtesting: index 1-minute and
 * 5-minute candles, the 1-minute -> 5-minute cross-check, 5-minute option data and the Multi-factor DEMA-WMA
 * Gainer backtest data suitability. Runs in the background (one at a time) and is kept as history; nothing is
 * ever repaired, deleted or backfilled from here.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import oiIntegrityAuditService, {
  ACTIVE_AUDIT_STATUSES,
  CATEGORY_LABEL,
  DATASET_LABEL,
  type AuditCategory,
  type AuditIssue,
  type AuditRun,
  type AuditUnderlying,
  type DemaActual,
  type DayRow,
  type IssuePage,
} from "@/services/oiIntegrityAuditService";
import { extractErrorMessage } from "@/utils/errors";

const STATUS_COLOR: Record<string, "default" | "info" | "warning" | "success" | "error"> = {
  PASS: "success", WARNING: "warning", FAIL: "error", NO_DATA: "error", NOT_EVALUATED: "default", INFO: "info",
  PENDING: "default", RUNNING: "info", CANCEL_REQUESTED: "warning", COMPLETED: "success", FAILED: "error", CANCELLED: "default",
  ERROR: "error",
};
const num = (v: number | null | undefined) => (v === null || v === undefined ? "—" : v.toLocaleString("en-IN"));
const pct = (v: number | null | undefined) => (v === null || v === undefined ? "—" : `${v}%`);
const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "—");
const todayIst = () => new Date(Date.now() + 330 * 60_000).toISOString().slice(0, 10);

function StatusChip({ status, testId }: { status: string | null | undefined; testId?: string }) {
  if (!status) return <>—</>;
  return <Chip size="small" label={status} color={STATUS_COLOR[status] ?? "default"} data-testid={testId} />;
}

function SummaryTable({ run }: { run: AuditRun }) {
  const m = run.summary?.metrics;
  if (!m) return null;
  const missing = m.missing_candles === null ? "—" : `${num(m.missing_candles)} / ${pct(m.missing_pct)}`;
  const rows: [string, string][] = [
    ["Expected candles", num(m.expected_candles)],
    ["Actual candles", num(m.actual_candles)],
    ["Missing candles", missing],
    ["Duplicate records", num(m.duplicate_records)],
    ["Invalid OHLC", num(m.invalid_ohlc)],
    ["Timestamp errors", num(m.timestamp_errors)],
    ["1m vs 5m mismatches", num(m.cross_mismatches)],
    ["Options coverage (historical candles)", pct(m.options_coverage_pct)],
    ["Options coverage (live snapshots, separate)", pct(m.options_snapshot_coverage_pct)],
    ["Backtest contracts without historical candles", num((m.options_contracts_historical_missing ?? 0) + (m.options_contracts_no_data ?? 0))],
    ["Out-of-session rows (information)",
      `${num(m.pre_open_rows_info)} pre-open · ${num(m.post_close_rows_info)} post-close · ${num(m.off_hours_rows_info)} other`],
    ["Missing OI", num(m.missing_oi)],
    ["Stale prices", num(m.stale_prices)],
  ];
  return (
    <Table size="small" sx={{ maxWidth: 460 }} data-testid="audit-summary">
      <TableHead><TableRow><TableCell>Metric</TableCell><TableCell align="right">Result</TableCell></TableRow></TableHead>
      <TableBody>
        {rows.map(([k, v]) => (
          <TableRow key={k}><TableCell>{k}</TableCell><TableCell align="right">{v}</TableCell></TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function keyNumbers(dataset: string, t: Record<string, number | null> | undefined): string {
  if (!t) return "";
  if (dataset === "OPTIONS_5M") {
    return `historical coverage ${pct(t.data_coverage_pct ?? t.coverage_pct)} · snapshots ${pct(t.snapshot_coverage_pct)} · `
      + `candles ${num(t.candle_rows)} / snapshots ${num(t.snapshot_rows)} · contracts missing ${num(t.contracts_historical_missing)}`
      + ` + ${num(t.contracts_no_data)} · missing OI ${num(t.missing_oi)}`;
  }
  return `expected ${num(t.expected)} · actual ${num(t.valid)} · missing ${num(t.missing)} · duplicates ${num(t.duplicates)}`;
}

function IndexResults({ run }: { run: AuditRun }) {
  const byIndex = run.summary?.by_index ?? {};
  return (
    <Grid container spacing={2}>
      {Object.entries(byIndex).map(([ix, res]) => (
        <Grid item xs={12} lg={6} key={ix}>
          <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 1.5 }} data-testid={`audit-index-${ix}`}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" fontWeight={700}>{ix}</Typography>
              <StatusChip status={res.status} />
            </Stack>
            <Table size="small">
              <TableHead>
                <TableRow><TableCell>Dataset</TableCell><TableCell>Status</TableCell><TableCell>Details</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(res.datasets).map(([ds, v]) => (
                  <TableRow key={ds}>
                    <TableCell>{DATASET_LABEL[ds] ?? ds}</TableCell>
                    <TableCell><StatusChip status={v.status} testId={`status-${ix}-${ds}`} /></TableCell>
                    <TableCell>
                      <Typography variant="body2">{keyNumbers(ds, v.totals)}</Typography>
                      {v.reasons.length > 0 && <Typography variant="caption" color="text.secondary">{v.reasons.join("; ")}</Typography>}
                      {Object.entries(v.sources ?? v.pairs ?? {}).map(([src, sv]) => (
                        <Typography key={src} variant="caption" display="block" color="text.secondary">
                          • {src}: {sv.status}{sv.primary ? " (primary)" : ""}{(sv.reasons ?? []).length ? ` — ${(sv.reasons ?? []).join("; ")}` : ""}
                          {sv.note ? ` — ${sv.note}` : ""}
                        </Typography>
                      ))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Grid>
      ))}
    </Grid>
  );
}

function actualText(a: DemaActual | undefined): string {
  if (!a || !a.decisions) return a?.days_not_run ? `not run (${a.days_not_run} day(s))` : "—";
  const reasons = Object.entries(a.unpriced_reasons ?? {}).map(([k, n]) => `${k} ${n}`).join(", ");
  return `${num(a.decisions)}: ${num(a.priced_historical)} historical · ${num(a.priced_snapshot)} snapshot · `
    + `${num(a.unpriced)} unpriced${reasons ? ` (${reasons})` : ""}`;
}

function DemaSection({ run }: { run: AuditRun }) {
  const d = run.dema;
  if (!d) {
    return run.category === "ALL" ? null : (
      <Typography variant="body2" color="text.secondary">DEMA-WMA suitability is evaluated with “All datasets”.</Typography>
    );
  }
  return (
    <Box data-testid="audit-dema">
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={700}>Multi-factor DEMA-WMA Gainer — backtest data suitability</Typography>
        <StatusChip status={d.status} />
        {d.config_version != null && <Typography variant="caption" color="text.secondary">active config v{d.config_version}</Typography>}
      </Stack>
      <Table size="small" sx={{ mb: 1.5 }}>
        <TableHead>
          <TableRow>
            <TableCell>Index</TableCell><TableCell>Status</TableCell><TableCell align="right">Decision times</TableCell>
            <TableCell align="right">Priceable (historical)</TableCell>
            <TableCell align="right">Missing data (FAIL)</TableCell>
            <TableCell align="right">5-min grid (structural)</TableCell>
            <TableCell align="right">Snapshot only</TableCell>
            <TableCell align="right">Actual backtest decisions</TableCell><TableCell align="right">Median lag</TableCell>
            <TableCell align="right">Price before signal (median age)</TableCell><TableCell>1-min source per session</TableCell><TableCell>Notes</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.entries(d.by_index).map(([ix, v]) => (
            <TableRow key={ix}>
              <TableCell>{ix}</TableCell>
              <TableCell><StatusChip status={v.status} /></TableCell>
              <TableCell align="right">{num(v.decisions)}</TableCell>
              <TableCell align="right" data-testid={`dema-hist-${ix}`}>{num(v.priced_historical ?? v.priced)} ({pct(v.historical_coverage_pct ?? v.executable_coverage_pct)})</TableCell>
              <TableCell align="right" data-testid={`dema-gap-${ix}`}>
                {v.data_gap_decisions == null ? "—" : `${num(v.data_gap_decisions)} (${pct(v.data_gap_pct)})`}
              </TableCell>
              <TableCell align="right" data-testid={`dema-grid-${ix}`}>
                {v.grid_lag_decisions == null ? "—" : `${num(v.grid_lag_decisions)} (${pct(v.grid_lag_pct)})`}
                {v.data_completeness_pct != null && (
                  <Typography variant="caption" display="block" color="text.secondary">
                    {pct(v.data_completeness_pct)} of reachable decisions priced
                  </Typography>
                )}
              </TableCell>
              <TableCell align="right">{num(v.priced_snapshot)}</TableCell>
              <TableCell align="right" data-testid={`dema-actual-${ix}`}>{actualText(v.actual)}</TableCell>
              <TableCell align="right">{v.entry_lag_median_s == null ? "—" : `${v.entry_lag_median_s}s`}</TableCell>
              <TableCell align="right">{v.prior_price_age_median_s == null ? "—" : `${v.prior_price_age_median_s}s (${pct(v.prior_price_older_than_60s_pct)} > 60s)`}</TableCell>
              <TableCell>
                {Object.entries(v.sessions_by_source ?? {}).map(([s, n]) => `${s} ${n}`).join(", ") || "—"}
                {(v.live_override_sessions ?? 0) > 0 && (
                  <Typography variant="caption" display="block" color="warning.main">
                    live overrides complete Breeze on {v.live_override_sessions} session(s)
                  </Typography>
                )}
              </TableCell>
              <TableCell><Typography variant="caption">{v.reasons.join("; ")}</Typography></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {d.source_proposal && (
        <Box sx={{ mb: 1.5 }} data-testid="audit-dema-source">
          <Typography variant="subtitle2">Signal source selection — {d.source_proposal.status}</Typography>
          <Typography variant="body2">{d.source_proposal.current_behaviour}</Typography>
          <Typography variant="body2" color="text.secondary">{d.source_proposal.effect}</Typography>
          {d.source_proposal.proposal.map((p) => <Typography key={p} variant="caption" display="block">• {p}</Typography>)}
        </Box>
      )}
      <Typography variant="subtitle2" gutterBottom>What the backtest consumes</Typography>
      <Table size="small" data-testid="audit-dema-trace">
        <TableHead><TableRow><TableCell>Item</TableCell><TableCell>Check</TableCell><TableCell>Finding</TableCell><TableCell>Code</TableCell></TableRow></TableHead>
        <TableBody>
          {d.trace.map((t) => (
            <TableRow key={t.item}>
              <TableCell>{t.item}</TableCell>
              <TableCell><StatusChip status={t.status} /></TableCell>
              <TableCell><Typography variant="body2">{t.finding}</Typography></TableCell>
              <TableCell><code>{t.module}</code></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function DayTable({ rows }: { rows: DayRow[] }) {
  const [page, setPage] = useState(0);
  const [onlyProblems, setOnlyProblems] = useState(true);
  const [dataset, setDataset] = useState("");
  const shown = useMemo(() => rows.filter((r) => (!onlyProblems || r.status !== "PASS") && (!dataset || r.dataset === dataset)), [rows, onlyProblems, dataset]);
  const datasets = Array.from(new Set(rows.map((r) => r.dataset)));
  return (
    <Box data-testid="audit-days">
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2">Date-wise breakdown ({shown.length} of {rows.length})</Typography>
        <TextField select size="small" label="Dataset" value={dataset} onChange={(e) => { setDataset(e.target.value); setPage(0); }} sx={{ minWidth: 200 }}>
          <MenuItem value="">All</MenuItem>
          {datasets.map((d) => <MenuItem key={d} value={d}>{DATASET_LABEL[d] ?? d}</MenuItem>)}
        </TextField>
        <Button size="small" onClick={() => { setOnlyProblems(!onlyProblems); setPage(0); }} data-testid="days-toggle">
          {onlyProblems ? "Show all days" : "Only days with issues"}
        </Button>
      </Stack>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell><TableCell>Index</TableCell><TableCell>Dataset</TableCell><TableCell>Source used</TableCell>
              <TableCell align="right">Expected</TableCell><TableCell align="right">Actual</TableCell><TableCell align="right">Missing</TableCell>
              <TableCell align="right">Errors / warnings</TableCell><TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shown.slice(page * 25, page * 25 + 25).map((r) => (
              <TableRow key={`${r.date}-${r.index}-${r.dataset}-${r.source}`}>
                <TableCell>{r.date}{r.trading_day ? "" : " (closed)"}</TableCell>
                <TableCell>{r.index}</TableCell>
                <TableCell>{DATASET_LABEL[r.dataset] ?? r.dataset}</TableCell>
                <TableCell><Typography variant="caption">{r.source ?? "—"}</Typography></TableCell>
                <TableCell align="right">{num(r.expected)}</TableCell>
                <TableCell align="right">{num(r.actual)}{r.coverage_pct != null ? ` (${r.coverage_pct}%)` : ""}</TableCell>
                <TableCell align="right" title={(r.missing_ranges ?? []).join(", ")}>{num(r.missing)}</TableCell>
                <TableCell align="right">{r.errors} / {r.warnings}</TableCell>
                <TableCell><StatusChip status={r.status} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination component="div" count={shown.length} page={page} rowsPerPage={25} rowsPerPageOptions={[25]}
          onPageChange={(_e, p) => setPage(p)} />
      </TableContainer>
    </Box>
  );
}

function IssuesTable({ run }: { run: AuditRun }) {
  const [severity, setSeverity] = useState("ERROR");
  const [dataset, setDataset] = useState("");
  const [index, setIndex] = useState("");
  const [page, setPage] = useState(0);
  const [data, setData] = useState<IssuePage | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    oiIntegrityAuditService.issues(run.id, { severity, dataset, index, offset: page * 50, limit: 50 })
      .then((d) => { if (live) setData(d); })
      .catch((e) => { if (live) setError(extractErrorMessage(e)); });
    return () => { live = false; };
  }, [run.id, severity, dataset, index, page]);
  const show = (i: AuditIssue) => Object.entries(i.values ?? {}).map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : String(v)}`).join(" · ");
  return (
    <Box data-testid="audit-issues">
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
        <Typography variant="subtitle2">Detailed error records</Typography>
        <TextField select size="small" label="Severity" value={severity} onChange={(e) => { setSeverity(e.target.value); setPage(0); }} sx={{ minWidth: 130 }}>
          {["", "ERROR", "WARNING", "INFO"].map((s) => <MenuItem key={s} value={s}>{s || "All"}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Dataset" value={dataset} onChange={(e) => { setDataset(e.target.value); setPage(0); }} sx={{ minWidth: 200 }}>
          <MenuItem value="">All</MenuItem>
          {Object.entries(DATASET_LABEL).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Index" value={index} onChange={(e) => { setIndex(e.target.value); setPage(0); }} sx={{ minWidth: 130 }}>
          <MenuItem value="">All</MenuItem>
          {run.symbol_codes.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
      {run.issues_truncated && (
        <Alert severity="info" sx={{ mb: 1 }}>Only the first records per severity are stored; the counts by code below are complete.</Alert>
      )}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date / time (IST)</TableCell><TableCell>Index</TableCell><TableCell>Dataset</TableCell><TableCell>Code</TableCell>
              <TableCell>Severity</TableCell><TableCell>Source / contract</TableCell><TableCell align="right">Count</TableCell><TableCell>Detail</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data?.items ?? []).map((i, k) => (
              <TableRow key={`${i.code}-${i.date}-${i.ts}-${k}`}>
                <TableCell>{i.ts ? i.ts.replace("T", " ") : i.date}</TableCell>
                <TableCell>{i.index}</TableCell>
                <TableCell>{DATASET_LABEL[i.dataset] ?? i.dataset}</TableCell>
                <TableCell><code>{i.code}</code></TableCell>
                <TableCell><StatusChip status={i.severity} /></TableCell>
                <TableCell><Typography variant="caption">{[i.source, i.contract].filter(Boolean).join(" · ") || "—"}</Typography></TableCell>
                <TableCell align="right">{num(i.count)}</TableCell>
                <TableCell>
                  <Typography variant="body2">{i.detail}</Typography>
                  {Object.keys(i.values ?? {}).length > 0 && <Typography variant="caption" color="text.secondary">{show(i)}</Typography>}
                </TableCell>
              </TableRow>
            ))}
            {data && data.items.length === 0 && (
              <TableRow><TableCell colSpan={8} align="center">No records for this filter.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination component="div" count={data?.total ?? 0} page={page} rowsPerPage={50} rowsPerPageOptions={[50]}
          onPageChange={(_e, p) => setPage(p)} />
      </TableContainer>
      {(run.issue_counts ?? []).length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="subtitle2" gutterBottom>Counts by code</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap data-testid="audit-counts">
            {(run.issue_counts ?? []).slice(0, 40).map((c) => (
              <Chip key={`${c.index}-${c.dataset}-${c.code}-${c.severity}`} size="small" variant="outlined"
                color={STATUS_COLOR[c.severity] ?? "default"} label={`${c.index} ${c.code} ${num(c.count)}`} />
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
}

function RunDetail({ run, onDownload }: { run: AuditRun; onDownload: () => void }) {
  return (
    <Stack spacing={2} data-testid="audit-detail">
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="subtitle1" fontWeight={700}>Audit result</Typography>
        <StatusChip status={run.overall_status} testId="audit-overall" />
        <Typography variant="body2" color="text.secondary">
          {run.symbol_codes.join(" + ")} · {CATEGORY_LABEL[run.category]} · {run.from_date} → {run.to_date} · executed {when(run.completed_at ?? run.started_at)}
          {run.created_by ? ` by ${run.created_by}` : ""}
        </Typography>
        <Box flexGrow={1} />
        <Button size="small" variant="outlined" onClick={onDownload} data-testid="audit-csv">Download CSV report</Button>
      </Stack>
      <Alert severity={run.overall_status === "PASS" ? "success" : run.overall_status === "WARNING" ? "warning" : "error"}>
        {run.overall_status === "PASS" && "No material integrity issue detected in the selected data."}
        {run.overall_status === "WARNING" && "Minor issues or incomplete coverage: review before relying on this data for backtesting."}
        {run.overall_status === "FAIL" && "Serious data issues: unsuitable for backtesting without investigation."}
      </Alert>
      <SummaryTable run={run} />
      <IndexResults run={run} />
      <DemaSection run={run} />
      <DayTable rows={run.day_rows ?? []} />
      <IssuesTable run={run} />
    </Stack>
  );
}

export default function DataIntegrityAuditSection() {
  const [underlying, setUnderlying] = useState<AuditUnderlying>("BOTH");
  const [fromDate, setFromDate] = useState(() => new Date(Date.now() + 330 * 60_000 - 30 * 86_400_000).toISOString().slice(0, 10));
  const [toDate, setToDate] = useState(todayIst());
  const [category, setCategory] = useState<AuditCategory>("ALL");
  const [runs, setRuns] = useState<AuditRun[]>([]);
  const [detail, setDetail] = useState<AuditRun | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setRuns(await oiIntegrityAuditService.listRuns(20));
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const active = runs.find((r) => ACTIVE_AUDIT_STATUSES.includes(r.status));
  useEffect(() => {
    if (!active) return undefined;
    const t = globalThis.setInterval(() => { void load(); }, 4000);
    return () => globalThis.clearInterval(t);
  }, [active, load]);

  const view = async (id: string) => {
    setError(null);
    try {
      setDetail(await oiIntegrityAuditService.getRun(id));
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  };

  const start = async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await oiIntegrityAuditService.start({ underlying, from_date: fromDate, to_date: toDate, category });
      setNotice("Audit started (read-only). Progress is shown below; large ranges can take several minutes.");
      setDetail(null);
      await load();
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (id: string) => {
    try {
      await oiIntegrityAuditService.cancel(id);
      await load();
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  };

  const download = async (id: string) => {
    try {
      await oiIntegrityAuditService.downloadReport(id);
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  };

  const invalidRange = !fromDate || !toDate || fromDate > toDate || toDate > todayIst();
  return (
    <Card variant="outlined" sx={{ mt: 3 }} data-testid="integrity-audit">
      <CardContent>
        <Typography variant="h6" gutterBottom>Breeze Data Integrity Audit</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Independently checks the stored historical data for completeness, accuracy and consistency before it is used for
          backtesting: index 1-minute and 5-minute candles (missing, duplicate, invalid, mis-timed, stale), aggregated
          1-minute vs stored 5-minute candles, 5-minute option data (coverage, OI, expiry / strike mapping) and the
          DEMA-WMA backtest's data suitability. Read-only: it never repairs, deletes or backfills data.
        </Typography>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }} sx={{ mb: 2 }}>
          <TextField select size="small" label="Underlying" value={underlying} sx={{ minWidth: 160 }}
            onChange={(e) => setUnderlying(e.target.value as AuditUnderlying)} inputProps={{ "data-testid": "audit-underlying" }}>
            <MenuItem value="NIFTY">NIFTY</MenuItem>
            <MenuItem value="BANKNIFTY">BANKNIFTY</MenuItem>
            <MenuItem value="BOTH">Both</MenuItem>
          </TextField>
          <TextField size="small" type="date" label="Start date" value={fromDate} InputLabelProps={{ shrink: true }}
            onChange={(e) => setFromDate(e.target.value)} />
          <TextField size="small" type="date" label="End date" value={toDate} InputLabelProps={{ shrink: true }}
            onChange={(e) => setToDate(e.target.value)} />
          <TextField select size="small" label="Data category" value={category} sx={{ minWidth: 230 }}
            onChange={(e) => setCategory(e.target.value as AuditCategory)}>
            {(Object.keys(CATEGORY_LABEL) as AuditCategory[]).map((c) => <MenuItem key={c} value={c}>{CATEGORY_LABEL[c]}</MenuItem>)}
          </TextField>
          <Button variant="contained" onClick={() => void start()} disabled={busy || Boolean(active) || invalidRange} data-testid="audit-run">
            Run Audit
          </Button>
        </Stack>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {notice && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}
        {active && (
          <Box sx={{ mb: 2 }} data-testid="audit-progress">
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2">
                Audit {active.status.toLowerCase()} — {num(active.progress_done)} of {num(active.progress_total)} index-days
                {active.current_step ? ` (last: ${active.current_step})` : ""}
              </Typography>
              <Button size="small" color="warning" onClick={() => void cancel(active.id)} disabled={active.status !== "RUNNING" && active.status !== "PENDING"}>
                Cancel
              </Button>
            </Stack>
            <LinearProgress variant="determinate" value={active.progress_total ? (100 * active.progress_done) / active.progress_total : 0} />
          </Box>
        )}
        <Typography variant="subtitle2" gutterBottom>Previous audits</Typography>
        <TableContainer sx={{ mb: 2 }}>
          <Table size="small" data-testid="audit-history">
            <TableHead>
              <TableRow>
                <TableCell>Executed</TableCell><TableCell>By</TableCell><TableCell>Range</TableCell><TableCell>Underlying</TableCell>
                <TableCell>Category</TableCell><TableCell>Run</TableCell><TableCell>Result</TableCell><TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {runs.map((r) => (
                <TableRow key={r.id} selected={detail?.id === r.id}>
                  <TableCell>{when(r.completed_at ?? r.started_at ?? r.created_at)}</TableCell>
                  <TableCell>{r.created_by ?? "—"}</TableCell>
                  <TableCell>{r.from_date} → {r.to_date}</TableCell>
                  <TableCell>{r.symbol_codes.join(" + ")}</TableCell>
                  <TableCell>{CATEGORY_LABEL[r.category] ?? r.category}</TableCell>
                  <TableCell><StatusChip status={r.status} /></TableCell>
                  <TableCell><StatusChip status={r.overall_status} /></TableCell>
                  <TableCell>
                    {r.status === "COMPLETED" && <Button size="small" onClick={() => void view(r.id)}>View</Button>}
                    {r.error && <Typography variant="caption" color="error">{r.error}</Typography>}
                  </TableCell>
                </TableRow>
              ))}
              {runs.length === 0 && <TableRow><TableCell colSpan={8} align="center">No audits yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
        {detail && <RunDetail run={detail} onDownload={() => void download(detail.id)} />}
      </CardContent>
    </Card>
  );
}

